import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Clock3 } from "lucide-react";
import { MaskLines } from "./Reveal";
import { IMG } from "@/data/content";

const Ecg = () => (
  <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="absolute left-0 right-0 top-[58%] w-full h-24 opacity-40 pointer-events-none" aria-hidden="true">
    <path className="ecg-path" d="M0 60 H420 L450 60 L470 20 L495 105 L520 8 L545 80 L565 60 H1200" fill="none" stroke="#38BDF8" strokeWidth="1.5" />
  </svg>
);

const Portrait = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div onMouseMove={onMove} onMouseLeave={reset} style={{ perspective: 1200 }} className="relative">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.4, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full rounded-b-3xl bg-[#112240]" data-testid="hero-portrait">
        <motion.img src={IMG.hero} alt="Registered nurse in uniform" initial={{ scale: 1.3 }} animate={{ scale: 1.05 }}
          transition={{ duration: 2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-transparent to-transparent" />
      </motion.div>
      <motion.div style={{ x: useTransform(ry, (v) => v * 2), y: useTransform(rx, (v) => v * -2) }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute -left-4 sm:-left-10 bottom-10 rounded-2xl bg-white p-4 pr-6 shadow-2xl shadow-black/30 flex items-center gap-3" data-testid="hero-shift-card">
        <span className="relative grid place-items-center w-10 h-10 rounded-full bg-emerald-50">
          <span className="absolute w-3 h-3 rounded-full bg-emerald-500 ping-soft" />
          <span className="w-3 h-3 rounded-full bg-emerald-500" />
        </span>
        <span>
          <span className="block text-xs text-slate-500 font-mono-x">Shift filled · 02:14</span>
          <span className="block text-sm font-bold text-[#0A192F]">RGN, Night · Nottingham</span>
        </span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return (
    <section ref={ref} data-testid="hero-section" className="relative bg-[#0A192F] text-white overflow-hidden grain">
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute -top-40 right-[-10%] w-[60vw] h-[60vw] rounded-full bg-sky-500/10 blur-[120px]" />
      <Ecg />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-14 lg:gap-8 items-end">
        <motion.div style={{ y, opacity: fade }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="eyebrow text-sky-300 flex items-center gap-3 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400" /> East Midlands · 24/7 clinical cover
          </motion.p>
          <MaskLines as="h1" inView={false} delay={0.25} testId="hero-heading"
            className="text-[13vw] sm:text-7xl lg:text-[6.6rem] font-extrabold tracking-[-0.045em] leading-[0.92]"
            lines={["Care that", <>arrives <span className="font-serif-i font-normal text-sky-300 tracking-normal">calm,</span></>, "on time."]} />
          <motion.p data-testid="hero-subheading" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 max-w-lg text-base md:text-lg text-slate-300 leading-relaxed">
            Agency nurses for wards and care homes. Skilled carers for people at home. One local team covering Nottinghamshire to Rutland, every hour of the year.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact#enquiry" data-testid="btn-hero-enquiry" className="group inline-flex items-center gap-3 rounded-full bg-sky-400 text-[#0A192F] pl-7 pr-2 py-2 font-semibold hover:bg-white transition-colors">
              Request care or cover
              <span className="w-10 h-10 rounded-full bg-[#0A192F] text-white grid place-items-center transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
            </Link>
            <Link to="/services" data-testid="btn-hero-services" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3 font-semibold hover:bg-white/10 transition-colors">Our services</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-sky-300" /> NMC & DBS checked</span>
            <span className="flex items-center gap-2"><Clock3 size={16} className="text-sky-300" /> [4-hour] urgent response</span>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pl-8 max-w-md lg:max-w-none mx-auto w-full sm:w-4/5 lg:w-full">
          <Portrait />
        </div>
      </div>
    </section>
  );
};
